import { useState, useMemo, useEffect, useCallback } from 'react';
import { Eye, X, Undo2, Send, User, ChevronDown, ChevronUp, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { personalizeMessage } from '@/lib/utils/message';
import { applyCountryCode } from '@/lib/utils/phone';
import type { Recipient } from '@/types/messaging';

interface SendPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  /** `sendToOptedOut` (KAN-155): the agent ticked "Send to them anyway". */
  onConfirmSend: (recipientIds: string[], sendToOptedOut: boolean) => void;
  recipients: Recipient[];
  /** KAN-155: the numbers (as in `recipients`) that opted out (replied STOP).
   *  They are skipped unless the agent ticks "Send to them anyway". */
  optedOutPhones?: string[];
  body: string;
  signature?: string;
  hasPlaceholders: boolean;
  hasProperties: boolean;
  isTemplate: boolean;
  countryCode?: string;
  /**
   * When false (non-phone TPAs e.g. Intercom), the recipient `phone` slot
   * carries an opaque identifier (contact id, email) — skip country-code
   * normalisation and any phone-shaped formatting. Defaults to true.
   */
  isPhoneBased?: boolean;
}

/**
 * Preview dialog shown before multi-recipient sends.
 * Displays personalised message previews as cards for each recipient
 * and allows removing individual recipients before confirming.
 */
export function SendPreviewDialog({
  open,
  onClose,
  onConfirmSend,
  recipients,
  optedOutPhones,
  body,
  signature,
  hasPlaceholders,
  hasProperties,
  isTemplate,
  countryCode,
  isPhoneBased = true,
}: SendPreviewDialogProps) {
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set());
  const [sendToOptedOut, setSendToOptedOut] = useState(false);

  // Reset exclusions when dialog opens
  useEffect(() => {
    if (open) {
      setExcludedIds(new Set());
      setSendToOptedOut(false);
    }
  }, [open]);

  const optedOutSet = useMemo(() => new Set(optedOutPhones || []), [optedOutPhones]);

  const previewItems = useMemo(() => {
    return recipients.map((r) => {
      // For non-phone TPAs (Intercom), `r.phone` is an opaque contact id /
      // email and should not be passed through libphonenumber.
      const phone = isPhoneBased ? applyCountryCode(r.phone, countryCode) : r.phone;
      const recipient = { ...r, phone };
      const personalizedBody = personalizeMessage({
        body,
        recipient,
        signature,
        hasPlaceholders,
        hasProperties,
        isTemplate,
      });
      return {
        id: r.id,
        name: r.name,
        phone,
        body: personalizedBody,
        excluded: excludedIds.has(r.id),
        optedOut: optedOutSet.has(r.phone),
      };
    });
  }, [recipients, body, signature, hasPlaceholders, hasProperties, isTemplate, excludedIds, optedOutSet, countryCode, isPhoneBased]);

  const activeCount = previewItems.filter((p) => !p.excluded).length;
  // KAN-155: of the selected recipients, the ones who opted out — skipped unless
  // the agent ticks "Send to them anyway".
  const optedOutCount = previewItems.filter((p) => !p.excluded && p.optedOut).length;
  const sendCount = sendToOptedOut ? activeCount : activeCount - optedOutCount;

  // Auto-close if all removed
  useEffect(() => {
    if (open && recipients.length > 0 && activeCount === 0) {
      onClose();
    }
  }, [activeCount, open, recipients.length, onClose]);

  const toggleExclude = (id: string) => {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    const ids = previewItems.filter((p) => !p.excluded).map((p) => p.id);
    onConfirmSend(ids, sendToOptedOut);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md p-0 gap-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Eye className="h-4.5 w-4.5 text-primary" />
            Preview Messages
          </DialogTitle>
          <DialogDescription className="text-xs">
            {activeCount} of {recipients.length} recipient{recipients.length !== 1 ? 's' : ''} selected
          </DialogDescription>
        </DialogHeader>

        {/* KAN-155: how many opted out, and the one choice for all of them */}
        {optedOutCount > 0 && (
          <div
            data-testid="send-preview-optout"
            className="mx-4 mb-3 flex items-start gap-2 rounded-lg border border-amber-300/50 bg-amber-500/5 p-3"
          >
            <Ban className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
            <div className="flex-1 min-w-0 space-y-1.5">
              <p className="text-xs font-medium text-foreground">
                {optedOutCount} of {activeCount} recipient{activeCount !== 1 ? 's' : ''} opted out (replied STOP)
              </p>
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="send-preview-optout-send-anyway"
                  className="h-3.5 w-3.5 accent-primary"
                  checked={sendToOptedOut}
                  onChange={(e) => setSendToOptedOut(e.target.checked)}
                />
                Send to them anyway
              </label>
              {!sendToOptedOut && (
                <p className="text-[11px] text-muted-foreground">They will be skipped.</p>
              )}
            </div>
          </div>
        )}

        {/* Scrollable card list */}
        <div className="max-h-[50vh] overflow-y-auto px-4 pb-3 space-y-2.5 scrollbar-thin">
          {previewItems.map((item) => (
            <div
              key={item.id}
              className={`relative rounded-lg border transition-all ${
                item.excluded
                  ? 'border-border/50 bg-muted/30 opacity-50'
                  : 'border-border bg-card shadow-sm'
              }`}
            >
              {/* Card header — recipient info + action */}
              <div className="flex items-center gap-2.5 px-3.5 pt-3 pb-2">
                {/* Avatar */}
                <div
                  className={`flex h-7 w-7 items-center justify-center rounded-full shrink-0 ${
                    item.excluded ? 'bg-muted' : 'bg-primary/10'
                  }`}
                >
                  <User
                    className={`h-3.5 w-3.5 ${
                      item.excluded ? 'text-muted-foreground' : 'text-primary'
                    }`}
                  />
                </div>

                {/* Name & phone */}
                <div className="flex-1 min-w-0">
                  <span
                    className={`text-sm font-medium block truncate ${
                      item.excluded ? 'line-through text-muted-foreground' : 'text-foreground'
                    }`}
                  >
                    {item.name !== item.phone ? item.name : item.phone}
                  </span>
                  {item.name !== item.phone && (
                    <span className="text-[11px] text-muted-foreground tabular-nums">
                      {item.phone}
                    </span>
                  )}
                </div>

                {item.optedOut && !item.excluded && (
                  <span className="shrink-0 rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
                    Opted out
                  </span>
                )}

                {/* Remove / Undo */}
                <button
                  type="button"
                  onClick={() => toggleExclude(item.id)}
                  className={`shrink-0 rounded-md p-1.5 transition-colors ${
                    item.excluded
                      ? 'text-primary hover:bg-primary/10'
                      : 'text-muted-foreground hover:text-destructive hover:bg-destructive/10'
                  }`}
                  title={item.excluded ? 'Undo remove' : 'Remove from send'}
                >
                  {item.excluded ? (
                    <Undo2 className="h-3.5 w-3.5" />
                  ) : (
                    <X className="h-3.5 w-3.5" />
                  )}
                </button>
              </div>

              {/* Card body — message preview with expand toggle */}
              <PreviewBody body={item.body} excluded={item.excluded} />
            </div>
          ))}
        </div>

        {/* Footer */}
        <DialogFooter className="px-5 py-4 border-t border-border flex-row items-center gap-2 sm:justify-between">
          <Button variant="outline" size="sm" onClick={onClose} className="h-8 text-xs px-4">
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={sendCount === 0}
            className="h-8 text-xs px-4 gap-1.5"
          >
            <Send className="h-3.5 w-3.5" />
            Send All ({sendCount})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Expandable message preview body */
function PreviewBody({ body, excluded }: { body: string; excluded: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = body.length > 200;
  const displayText = isLong && !expanded ? body.slice(0, 200) + '...' : body;

  return (
    <div className={`group/body relative px-3.5 pb-3 ${excluded ? 'line-through' : ''}`}>
      <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-line">
        {displayText}
      </p>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="mt-1 flex items-center gap-0.5 text-[10px] font-medium text-primary hover:text-primary/80 transition-colors"
        >
          {expanded ? (
            <>Show less <ChevronUp className="h-3 w-3" /></>
          ) : (
            <>Show more <ChevronDown className="h-3 w-3" /></>
          )}
        </button>
      )}
    </div>
  );
}
